export type ReminderDeliveryMode = 'email-only' | 'email-sms'
export type ScheduledCallType = 'phone' | 'cliq'

export type ReminderPlanItem = {
  id: string
  kind: 'confirmation' | 'four-hour' | 'ten-minute'
  channel: 'email' | 'sms'
  sendAt: string
  sendAtIso?: string
  status: 'scheduled' | 'shifted' | 'suppressed'
  explanation: string
}

export type ReminderPlanInput = {
  date: string
  time: string
  timeZone: string
  callType: ScheduledCallType
  deliveryMode: ReminderDeliveryMode
  includeFourHour: boolean
  includeTenMinute: boolean
  recipientName: string
}

const SMS_QUIET_START_MINUTES = 20 * 60
const SMS_QUIET_END_MINUTES = 7 * 60 + 50

function parseMeeting(date: string, time: string): Date {
  const value = new Date(`${date}T${time}:00`)
  return Number.isNaN(value.getTime()) ? new Date() : value
}

function formatTime(value: Date): string {
  return value.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

function formatDateTime(value: Date): string {
  return value.toLocaleString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function minutesOfDay(value: Date): number {
  return value.getHours() * 60 + value.getMinutes()
}

function isSmsQuiet(value: Date): boolean {
  const minutes = minutesOfDay(value)
  return minutes < SMS_QUIET_END_MINUTES || minutes > SMS_QUIET_START_MINUTES
}

function atEightPm(value: Date): Date {
  const shifted = new Date(value)
  shifted.setHours(20, 0, 0, 0)
  return shifted
}

function previousDayAtEightPm(value: Date): Date {
  const shifted = new Date(value)
  shifted.setDate(shifted.getDate() - 1)
  shifted.setHours(20, 0, 0, 0)
  return shifted
}

function adjustFourHourSms(raw: Date): Date {
  const minutes = minutesOfDay(raw)
  if (minutes < SMS_QUIET_END_MINUTES) return previousDayAtEightPm(raw)
  if (minutes > SMS_QUIET_START_MINUTES) return atEightPm(raw)
  return raw
}

export function buildReminderPlan(input: ReminderPlanInput): ReminderPlanItem[] {
  const meeting = parseMeeting(input.date, input.time)
  const plan: ReminderPlanItem[] = []
  const now = new Date()

  plan.push({
    id: 'confirmation-email',
    kind: 'confirmation',
    channel: 'email',
    sendAt: 'Immediately',
    sendAtIso: now.toISOString(),
    status: 'scheduled',
    explanation: input.callType === 'cliq'
      ? 'Send the calendar invitation and Cliq meeting link immediately.'
      : 'Send the calendar invitation and callback details immediately.',
  })

  if (input.deliveryMode === 'email-sms') {
    if (!isSmsQuiet(now) && now < meeting) {
      plan.push({
        id: 'confirmation-sms',
        kind: 'confirmation',
        channel: 'sms',
        sendAt: 'Immediately',
        sendAtIso: now.toISOString(),
        status: 'scheduled',
        explanation: input.callType === 'cliq'
          ? 'Text the Cliq meeting link and reschedule instructions now.'
          : 'Text the callback time and reschedule instructions now.',
      })
    } else {
      const queued = minutesOfDay(now) > SMS_QUIET_START_MINUTES
        ? new Date(new Date(now).setDate(now.getDate() + 1))
        : new Date(now)
      queued.setHours(7, 50, 0, 0)
      if (queued < meeting) {
        plan.push({
          id: 'confirmation-sms',
          kind: 'confirmation',
          channel: 'sms',
          sendAt: formatDateTime(queued),
          sendAtIso: queued.toISOString(),
          status: 'shifted',
          explanation: 'The confirmation text is queued for the next allowed SMS window.',
        })
      } else {
        plan.push({
          id: 'confirmation-sms',
          kind: 'confirmation',
          channel: 'sms',
          sendAt: 'Not sent',
          status: 'suppressed',
          explanation: 'The confirmation text would arrive during quiet hours too close to the call, so email is used instead.',
        })
      }
    }
  }

  if (input.includeFourHour) {
    const rawFourHour = new Date(meeting.getTime() - 4 * 60 * 60 * 1000)
    plan.push({
      id: 'four-hour-email',
      kind: 'four-hour',
      channel: 'email',
      sendAt: formatDateTime(rawFourHour),
      sendAtIso: rawFourHour.toISOString(),
      status: 'scheduled',
      explanation: `Email reminder four hours before the ${input.callType === 'cliq' ? 'Cliq meeting' : 'phone call'}.`,
    })

    if (input.deliveryMode === 'email-sms') {
      const adjusted = adjustFourHourSms(rawFourHour)
      plan.push({
        id: 'four-hour-sms',
        kind: 'four-hour',
        channel: 'sms',
        sendAt: formatDateTime(adjusted),
        sendAtIso: adjusted.toISOString(),
        status: adjusted.getTime() === rawFourHour.getTime() ? 'scheduled' : 'shifted',
        explanation: adjusted.getTime() === rawFourHour.getTime()
          ? 'Text reminder four hours before the call.'
          : `The four-hour text would land at ${formatTime(rawFourHour)}, so it moves to 8:00 PM in the prior allowed window.`,
      })
    }
  }

  if (input.includeTenMinute) {
    const rawTenMinute = new Date(meeting.getTime() - 10 * 60 * 1000)
    plan.push({
      id: 'ten-minute-email',
      kind: 'ten-minute',
      channel: 'email',
      sendAt: formatDateTime(rawTenMinute),
      sendAtIso: rawTenMinute.toISOString(),
      status: 'scheduled',
      explanation: 'Final email reminder ten minutes before the call.',
    })

    if (input.deliveryMode === 'email-sms') {
      if (!isSmsQuiet(rawTenMinute)) {
        plan.push({
          id: 'ten-minute-sms',
          kind: 'ten-minute',
          channel: 'sms',
          sendAt: formatDateTime(rawTenMinute),
          sendAtIso: rawTenMinute.toISOString(),
          status: 'scheduled',
          explanation: 'Final text reminder ten minutes before the call.',
        })
      } else {
        plan.push({
          id: 'ten-minute-sms',
          kind: 'ten-minute',
          channel: 'sms',
          sendAt: 'Email only',
          status: 'suppressed',
          explanation: `The ten-minute reminder would land at ${formatTime(rawTenMinute)} during SMS quiet hours, so the final reminder remains email-only.`,
        })
      }
    }
  }

  return plan
}

export function buildReminderCommitmentLanguage(
  input: ReminderPlanInput,
  plan: ReminderPlanItem[],
): string {
  const meeting = parseMeeting(input.date, input.time)
  const fourEmail = plan.find(item => item.id === 'four-hour-email')
  const fourSms = plan.find(item => item.id === 'four-hour-sms')
  const tenEmail = plan.find(item => item.id === 'ten-minute-email')
  const tenSms = plan.find(item => item.id === 'ten-minute-sms')
  const destination = input.deliveryMode === 'email-sms'
    ? 'your email and mobile number'
    : 'your email'

  const sentences = [
    `I’ll send the ${input.callType === 'cliq' ? 'calendar invitation and Cliq meeting link' : 'calendar invitation and callback details'} to ${destination}.`,
  ]

  if (fourEmail) {
    if (fourSms?.status === 'shifted') {
      sentences.push(`Your four-hour email reminder is scheduled for ${fourEmail.sendAt}. Because the matching text would fall during quiet hours, the text will arrive at ${fourSms.sendAt} instead.`)
    } else if (fourSms?.status === 'scheduled') {
      sentences.push(`Your email and text reminders are scheduled four hours before the call at ${fourEmail.sendAt}.`)
    } else {
      sentences.push(`Your email reminder is scheduled four hours before the call at ${fourEmail.sendAt}.`)
    }
  }

  if (tenEmail) {
    if (tenSms?.status === 'scheduled') {
      sentences.push(`You’ll also receive a final email and text reminder ten minutes before at ${tenEmail.sendAt}.`)
    } else if (tenSms?.status === 'suppressed') {
      sentences.push(`You’ll receive the final email reminder ten minutes before at ${tenEmail.sendAt}; the text is skipped because that time falls inside SMS quiet hours.`)
    } else {
      sentences.push(`You’ll also receive a final email reminder ten minutes before at ${tenEmail.sendAt}.`)
    }
  }

  sentences.push(`The call is set for ${formatDateTime(meeting)}. If anything changes, please use the reschedule option or send a quick reply rather than leaving the time open.`)
  return sentences.join(' ')
}
